import type { DeliveryTabGridEntry } from "@/lib/api/dispatchTypes";
import { businessDayOf } from "@/shared/businessDay";
import { dispatchActions, isDispatchableBill, type BillDispatch } from "./dispatchBoard";

/**
 * The Delivery & takeaway tab's "Waiting for a driver" table (B6 · P24,
 * plan D-36 / D-37). Pure: which bags are waiting, in which order, and what
 * each row's kitchen chip and payment line say. The data is the off-premise
 * grid joined with the dispatch board on the client — no list endpoint.
 */

/**
 * WHO GETS «التوصيل والتيك أواي» (2026-10-28, founder: «the waiter is supposed
 * not to control the takeaway and delivery … his boss mainly focuses on the
 * restaurant»). Takeaway and delivery bags are run from the till: they end in a
 * close and a settle, and a waiter can do neither. So the tab is for a session
 * that may CLOSE a bill (`INVOICE_CLOSE`: cashier, manager, owner) or read the
 * dispatch board (`DISPATCH_MANAGE` / `DISPATCH_CASH_CONFIRM`) — no longer for
 * anyone who may merely OPEN one (D-35 gated on `INVOICE_OPEN`, which every
 * waiter holds for their tables). A role an owner gives `INVOICE_CLOSE` to
 * gets it back.
 */
export function offPremiseTabAvailable({
  canClose,
  canSeeBoard,
}: {
  canClose: boolean;
  canSeeBoard: boolean;
}): boolean {
  return canClose || canSeeBoard;
}

/**
 * A bag is WAITING when it is the restaurant's own delivery
 * (`isDispatchableBill`) and either nobody holds it (it may be offered) or its
 * live assignment is still an OFFER — an offered row stays in the table, with
 * its countdown and "Change" instead of the checkbox.
 */
export function isWaitingForDriver(
  bill: DeliveryTabGridEntry,
  entry: BillDispatch | undefined,
): boolean {
  if (!isDispatchableBill(bill)) return false;
  // G24b M3 (interim): an OPEN bag with nothing sent to the kitchen and no
  // driver on it — e.g. a «New delivery order» opened and abandoned — is not a
  // bag to deliver; it is listed under «Not sent yet» instead.
  if (isUnsentEmpty(bill, entry)) return false;
  // P24b (coordinator rule after the P23 re-gate): a CLOSED bag handed to a
  // TYPED rider has already left with that rider — it is on the rider's
  // settlement run (P25; until then the rider hand-in rows), never waiting.
  if (bill.status === "CLOSED" && isHandedToTypedCourier(bill)) return false;
  return dispatchActions(entry).assign || entry?.current?.status === "OFFERED";
}

/**
 * G24b M3 — an OPEN bill with no chargeable plate yet (the basket does not
 * count) and no live assignment. Plates unknown (an older API) is not empty.
 */
export function isUnsentEmpty(bill: DeliveryTabGridEntry, entry: BillDispatch | undefined): boolean {
  return bill.status === "OPEN" && bill.plates !== undefined && bill.plates !== null &&
    bill.plates.chargeable === 0 && !entry?.current;
}

/** What the kitchen column says about a bag. */
export type KitchenChip =
  | { kind: "ready"; minutes: number }
  | { kind: "preparing" }
  | { kind: "notSent" };

/**
 * "Ready Nm" from the server's `readySince` (every chargeable plate READY or
 * SERVED); "Preparing" once anything was fired; "Not sent" while nothing was.
 * Never judged from a device clock beyond the minutes counter itself.
 */
export function kitchenChip(bill: DeliveryTabGridEntry, now: number): KitchenChip {
  if (bill.readySince) {
    const since = Date.parse(bill.readySince);
    const minutes = Number.isFinite(since) ? Math.max(0, Math.floor((now - since) / 60_000)) : 0;
    return { kind: "ready", minutes };
  }
  const plates = bill.plates;
  if (!plates || plates.chargeable === 0) return { kind: "notSent" };
  return { kind: "preparing" };
}

/** What the bag still owes — `amountDue` once the API sends it, else the running total. */
export function waitingTotal(bill: Pick<DeliveryTabGridEntry, "amountDue" | "runningTotal">): number {
  return bill.amountDue ?? bill.runningTotal;
}

/**
 * The line under the total (D-37): "Cash on delivery" while something is owed,
 * "Paid" once a payment covered it. The grid carries no tender method, so the
 * design's "Card / InstaPay" is not drawn here (recorded deviation).
 */
export function paymentLine(bill: DeliveryTabGridEntry): "cod" | "paid" | null {
  if (waitingTotal(bill) > 0.004) return "cod";
  if ((bill.paidSoFar ?? 0) > 0) return "paid";
  return null;
}

export type WaitingRow = {
  bill: DeliveryTabGridEntry;
  entry: BillDispatch | undefined;
  kitchen: KitchenChip;
  /** An offer is out: the row carries "Change", not a checkbox. */
  offered: boolean;
};

/**
 * The waiting rows, kitchen-READY first, then oldest first (`openedAt`).
 * `lookup` is the board's per-bill reading (`useDispatchBoard().lookup`).
 */
export function waitingRows(
  bills: readonly DeliveryTabGridEntry[],
  lookup: (invoiceId: string) => BillDispatch | undefined,
  now: number,
): WaitingRow[] {
  return bills
    .map((bill) => ({ bill, entry: lookup(bill.invoiceId) }))
    .filter(({ bill, entry }) => isWaitingForDriver(bill, entry))
    .map(({ bill, entry }) => ({
      bill,
      entry,
      kitchen: kitchenChip(bill, now),
      offered: entry?.current?.status === "OFFERED",
    }))
    .sort((a, b) => {
      const ready = Number(b.kitchen.kind === "ready") - Number(a.kitchen.kind === "ready");
      if (ready !== 0) return ready;
      const opened = Date.parse(a.bill.openedAt) - Date.parse(b.bill.openedAt);
      return opened !== 0 ? opened : a.bill.dailySequence - b.bill.dailySequence;
    });
}

/**
 * The tab's alert tone (D-35): a bag the kitchen has READY that nobody holds —
 * not even an offer.
 */
export function hasReadyUnassigned(rows: readonly WaitingRow[]): boolean {
  return rows.some((row) => row.kitchen.kind === "ready" && !row.offered);
}

/**
 * Up to two letters for a driver's avatar: the first letter of each of the
 * first two words written in the same script as the name's first letter — so
 * «مندوب B6» reads «م», never a mixed «مB», and "Hassan Ali" reads "HA". A
 * one-word Latin name takes its first two letters. Latin is upper-cased.
 */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const firsts = words.map((word) => [...word][0]).filter((char) => /\p{L}/u.test(char));
  if (firsts.length === 0) return "?";
  const arabic = (char: string) => /\p{Script=Arabic}/u.test(char);
  const lead = firsts[0];
  const same = firsts.filter((char) => arabic(char) === arabic(lead)).slice(0, 2);
  if (same.length === 1 && !arabic(lead)) {
    const letters = [...words[0]].filter((char) => /\p{L}/u.test(char)).slice(0, 2);
    return letters.join("").toLocaleUpperCase();
  }
  return same.join("").toLocaleUpperCase();
}

/** The design's four avatar palettes, picked by a stable hash of the driver id. */
export function avatarTone(id: string): 0 | 1 | 2 | 3 {
  let hash = 0;
  for (const char of id) hash = (hash * 31 + char.charCodeAt(0)) | 0;
  return (Math.abs(hash) % 4) as 0 | 1 | 2 | 3;
}

/** A pasted link — any `http(s)://` token up to the next space. */
const LINK = /https?:\/\/\S+/gi;
/**
 * A bare coordinate pair, the other way a pin is typed (D-20): two decimal
 * numbers within lat/lng range, comma-separated — «31.2001, 29.9187». Both
 * need a decimal part, so "12, 3" (a building and a floor) is never taken.
 */
const LAT_LNG = /(?<![\d.])-?\d{1,3}\.\d+\s*,\s*-?\d{1,3}\.\d+(?![\d.])/g;
/** Separators a removed token can leave stranded. */
const SEPARATOR = /[,،·|\-–—/]/;

/**
 * What the «Deliver to» line may print of a typed address (B6 · P24 G24-M1):
 * the address with every pasted Maps link and bare `lat,lng` pair taken out —
 * a cashier reads a street, never a URL — and whether one was there, so the
 * row can draw a pin instead. Never builds a link from the typed text.
 */
export function addressForDisplay(raw: string | null | undefined): { text: string; hasPin: boolean } {
  const source = raw ?? "";
  let hasPin = false;
  const stripped = source
    .replace(LINK, () => {
      hasPin = true;
      return " ";
    })
    .replace(LAT_LNG, () => {
      hasPin = true;
      return " ";
    });
  // Tidy: one space between words, no separator left alone or doubled, none at
  // either end.
  const parts = stripped
    .split(/\s+/)
    .filter(Boolean)
    .reduce<string[]>((kept, token) => {
      const lone = token.length === 1 && SEPARATOR.test(token);
      if (lone && (kept.length === 0 || SEPARATOR.test(kept[kept.length - 1].slice(-1)))) return kept;
      kept.push(token);
      return kept;
    }, []);
  let text = parts.join(" ").replace(/\s+([,،])/g, "$1");
  text = text.replace(new RegExp(`^(?:${SEPARATOR.source}|\\s)+|(?:${SEPARATOR.source}|\\s)+$`, "g"), "");
  return { text, hasPin };
}

/**
 * B6 · P24b (user decision 2, 2026-10-18): the Delivery tab's «Pickup orders»
 * table — every live bill at no table that is collected AT THE COUNTER: a
 * takeaway (direct or a platform's pickup), and a platform's delivery, whose
 * courier is the platform's and collects here (D90). The restaurant's own
 * delivery bags are never here: they wait in «Waiting for a driver» and, once
 * a driver holds them, belong to the driver's settlement card (P25).
 */
export function isPickupBill(bill: DeliveryTabGridEntry): boolean {
  if (bill.status !== "OPEN" && bill.status !== "CLOSED") return false;
  if (bill.channel === "TAKEAWAY") return true;
  return bill.channel === "DELIVERY" && bill.aggregatorId !== null;
}

/**
 * A bag that left with a courier whose name was TYPED at hand-over (a rider
 * or a platform courier) — the server lists such a CLOSED delivery on that
 * courier's Rider / Platform settlement card (`listRiderBags`: blank names
 * never count).
 */
export function isHandedToTypedCourier(bill: DeliveryTabGridEntry): boolean {
  return Boolean(bill.handedTo?.trim());
}

/**
 * A pickup still OPEN from an earlier business day (founder 2026-10-27).
 * Off-premise, paid is not received: such a bill is never closed for the
 * cashier (its close is the hand-over), so it stays on the list — flagged,
 * for someone to hand it over, or cancel and refund it. Days are the
 * store's own (`dayStartHour`, timezone), as the server counts them.
 */
export function isFromEarlierDay(
  bill: Pick<DeliveryTabGridEntry, "status" | "openedAt">,
  now: Date,
  clock: { dayStartHour: number; timezone: string } | null,
): boolean {
  if (!clock || bill.status !== "OPEN") return false;
  const opened = new Date(bill.openedAt);
  if (Number.isNaN(opened.getTime())) return false;
  return businessDayOf(opened, clock.dayStartHour, clock.timezone) !== businessDayOf(now, clock.dayStartHour, clock.timezone);
}

export type PickupRow = { bill: DeliveryTabGridEntry; kitchen: KitchenChip };

/**
 * The pickup rows, kitchen-READY first (the customer is collecting), then
 * oldest. G25 L5 (P28a): `onCards` — the invoices a live settlement card
 * lists (`invoicesOnCards`) — are left out: a CLOSED platform delivery handed
 * to the platform's courier by a typed name is that courier's Platform card's
 * bag, never also a pickup row. Without the cards (no grant, still loading)
 * nothing is left out, so no bag is ever lost.
 */
export function pickupRows(
  bills: readonly DeliveryTabGridEntry[],
  now: number,
  onCards: ReadonlySet<string> = new Set(),
): PickupRow[] {
  return bills
    .filter((bill) => isPickupBill(bill) && !onCards.has(bill.invoiceId))
    .map((bill) => ({ bill, kitchen: kitchenChip(bill, now) }))
    .sort((a, b) => {
      const ready = Number(b.kitchen.kind === "ready") - Number(a.kitchen.kind === "ready");
      if (ready !== 0) return ready;
      const opened = Date.parse(a.bill.openedAt) - Date.parse(b.bill.openedAt);
      return opened !== 0 ? opened : a.bill.dailySequence - b.bill.dailySequence;
    });
}

/**
 * The Delivery tab's count and tone, ONE rule for the floor's tab and the
 * register's (P24b): the bags waiting for a driver — read only from a board
 * that answered — or, for a session without the board, the live bills at no
 * table.
 */
export function deliveryTabBadge(
  bills: readonly DeliveryTabGridEntry[],
  canSeeBoard: boolean,
  rows: readonly WaitingRow[],
): { count: number; tone: "alert" | "info" } {
  return {
    // G24b M3: a bill with nothing sent yet is not counted either way.
    count: canSeeBoard ? rows.length : bills.filter((bill) => !isUnsentEmpty(bill, undefined)).length,
    tone: hasReadyUnassigned(rows) ? "alert" : "info",
  };
}

/** Statuses where a driver HOLDS the bag (an OFFER still waits in the waiting table). */
const HELD = new Set(["ACCEPTED", "PICKED_UP", "ARRIVED", "FAILED"]);

export type WithDriverRow = WaitingRow;

/**
 * G24b M1 — «With a driver»: every own bag whose live assignment is past the
 * offer (accepted, picked up, at the door, or failed with a report), OPEN or
 * CLOSED, so its chip, Change / Follow up / End delivery / tracking share and
 * the bill stay reachable on the PAPER till. An OFFERED bag stays in «Waiting
 * for a driver» (its countdown and Change are there). INTERIM: P25 folds these
 * into the drivers' settlement cards.
 */
export function withDriverRows(
  bills: readonly DeliveryTabGridEntry[],
  lookup: (invoiceId: string) => BillDispatch | undefined,
  now: number,
): WithDriverRow[] {
  return bills
    .map((bill) => ({ bill, entry: lookup(bill.invoiceId) }))
    .filter(({ bill, entry }) => isDispatchableBill(bill) && HELD.has(entry?.current?.status ?? ""))
    .map(({ bill, entry }) => ({ bill, entry, kitchen: kitchenChip(bill, now), offered: false }))
    .sort((a, b) => {
      // A reported problem first, then the oldest.
      const problem = Number(Boolean(b.entry?.current?.problemOpen)) - Number(Boolean(a.entry?.current?.problemOpen));
      if (problem !== 0) return problem;
      const opened = Date.parse(a.bill.openedAt) - Date.parse(b.bill.openedAt);
      return opened !== 0 ? opened : a.bill.dailySequence - b.bill.dailySequence;
    });
}

/** G24b M3 — own delivery bills opened with nothing sent yet (listed so they can be finished or cancelled). */
export function unsentOwnRows(
  bills: readonly DeliveryTabGridEntry[],
  lookup: (invoiceId: string) => BillDispatch | undefined,
  now: number,
): PickupRow[] {
  return bills
    .filter((bill) => isDispatchableBill(bill) && isUnsentEmpty(bill, lookup(bill.invoiceId)))
    .map((bill) => ({ bill, kitchen: kitchenChip(bill, now) }));
}
